/**
 * Routed away tab — everything Merlin (or Jess) sent off Jess's desk and
 * still open with someone else: who has it, since when, the routing reason,
 * and whether they wrote back about it by email. Each row opens
 * the thread's workspace; "Pull back" asks once, under the row, then brings
 * it back to your queue without leaving the list (Phase 2; the ask since the
 * fan-out mapping, where a careful reader pulled one back by accident).
 *
 * What this tab does NOT claim: a recipient-side "opened / finished" state.
 * The owner declined the per-recipient handoff layer on 2026-09-02 (review
 * 03hzde, PR #2085 closed unmerged) — once a thread is routed away it is
 * off Jess's desk; this tab is visibility, not a second inbox.
 *
 * Closed threads (Done / Dismissed) have their own Done tab.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ageLabel } from '../../components/portal/threadTriageView';
import { failedActionMessage } from '../../components/portal/investigationsView';
import { trackEvent } from '../../services/portalTracking';
import { rerouteThread, type DoorThreadRead } from '../../services/qcpInvestigations';
import { joinNames, neverEmailed, undeliveredFirst, type DeliveryOutcome } from './deliveryView';
import { assignedByLine } from './routeOriginView';
import { useDeliveryOutcomes } from './useDeliveryOutcomes';
import { routedAwayLine, routedAwayThreads } from './qualityTriageView';
import { asksForThread, fromTab, quietDays, threadWorkspacePath } from './qualityWorkspaceView';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { shortExcerpt } from './handOffView';
import {
  answerWords,
  followUpWords,
  followUpsSinceSend,
  latestAnswer,
  noReplyWords,
  sentByWords,
} from './routedRowView';
import { useQualityTriage } from './qualityTriageContext';
import { handOffReply, respondedLine } from './respondedView';
import { latestNoteLine } from '../../components/portal/noteBoxView';
import { ThreadRefLabel } from './ThreadRefLabel';
import FilterChip from './FilterChip';
import { namesInclude } from './listFilter';
import { routedAwayPeople, routedAwayWith, routedNudgeBlock, routedTargets, type NudgeTarget } from './nudgeView';
import { useNudge } from './useNudge';
import { itemTargetAttr } from './itemNav';
import { NudgeBelow, NudgeButtons, NudgePerson, NudgedChip } from './NudgeControls';
import styles from './QualityTriagePages.module.css';

function RoutedRow({
  thread,
  asks,
  userName,
  delivery,
  canPullBack,
  busy,
  confirming,
  error,
  onOpen,
  onAskPullBack,
  onPullBack,
  onCancel,
  actions = null,
  below = null,
}: {
  thread: DoorThreadRead;
  /** The door's asks on this item (asksForThread). */
  asks: DoorDataRequestRead[];
  /** The viewer: "Sent by you" against the router's sends. */
  userName: string | null;
  /** Did the hand-off email reach each person; null/undefined = unknown. */
  delivery: DeliveryOutcome | null | undefined;
  canPullBack: boolean;
  busy: boolean;
  /** The "pull it back?" step is open under this row. */
  confirming: boolean;
  /** Why the last pull-back of THIS row failed, shown under it. */
  error: string | null;
  onOpen: (thread: DoorThreadRead) => void;
  onAskPullBack: (thread: DoorThreadRead) => void;
  onPullBack: (thread: DoorThreadRead) => void;
  onCancel: () => void;
  /** More buttons beside the row (Nudge). */
  actions?: ReactNode;
  /** More under the row (the nudge's confirm step or note). */
  below?: ReactNode;
}) {
  // They answered the hand-off email (notes round): the row says so, and a
  // reply is not "nothing in N days".
  const reply = handOffReply(thread);
  const stale = reply ? null : quietDays(thread);
  const note = latestNoteLine(thread.latest_note, thread.note_count ?? 0);
  const missed = delivery?.undelivered ?? [];
  // The system assigned it and emailed nobody (card 75bf026f): the backend
  // says so, since the timeline has no delivery line to read. The row then
  // says where the route came from (review 2r8pgd), not "send it".
  const unsent = missed.length === 0 && neverEmailed(thread);
  // Door gate (kanban 8a4d2212): the hand-off is waiting for Merlin's
  // "already known?" check, or Merlin held it. Not emailed by design, so
  // neither the alarm nor "Sent"; the row says which, and the item offers
  // Send anyway.
  const gated = missed.length === 0 && (thread.route_notified === 'pending' || thread.route_notified === 'held');
  const heldByMerlin = gated && thread.route_notified === 'held';
  // UX round 2026-10-13 (status sweep): who sent it, what was last
  // answered on it, and whether it was followed up since the send.
  const answer = latestAnswer(thread, asks);
  const followUp = followUpsSinceSend(thread, asks);
  const noReply = noReplyWords(!!reply, answer);
  const sentBy = unsent || gated ? null : sentByWords(thread, userName);
  const replyExcerpt = reply ? shortExcerpt(thread.latest_route_reply?.excerpt, 120) : '';
  return (
    <div className={styles.rowGroup}>
      <div className={styles.rowWrap}>
        <button
          type="button"
          className={`${styles.row} ${missed.length > 0 || unsent ? styles.rowUndelivered : ''}`}
          onClick={() => onOpen(thread)}
          data-action="qcp_item_open"
          {...{ [itemTargetAttr(thread.thread_no)]: thread.thread_no }}
        >
          {missed.length > 0 ? (
            <span className={`${styles.sev} ${styles.sev_undelivered}`}>NOT DELIVERED</span>
          ) : unsent ? (
            <span className={`${styles.sev} ${styles.sev_undelivered}`}>NEVER EMAILED</span>
          ) : gated ? (
            <span className={`${styles.sev} ${styles.sev_watch}`}>{heldByMerlin ? 'HELD' : 'CHECKING'}</span>
          ) : (
            <span className={`${styles.sev} ${styles.sev_watch}`}>SENT</span>
          )}
          <span className={styles.rowBody}>
            <span className={styles.rowTitle}>{thread.title}</span>
            {missed.length > 0 && (
              <span className={styles.undeliveredLine} data-testid="row-not-delivered">
                ⚠ Never reached {joinNames(missed.map((p) => p.name))}
                {delivery?.nobodyDelivered ? ': nobody has this item.' : '.'} Open it to send it again.
              </span>
            )}
            {unsent && (
              /* Not "send it now" (owner, review 2r8pgd): say where the
                 route came from. */
              <span className={styles.undeliveredLine} data-testid="row-never-emailed">
                ⚠ Never emailed. {assignedByLine(thread)} {thread.routed_to || 'They'} do not know
                they have this item.
              </span>
            )}
            {gated && (
              <span className={styles.rowSub} data-testid="row-handoff-gate">
                {heldByMerlin
                  ? `✋ Not emailed: Merlin judged ${thread.routed_to || 'they'} already know${thread.routed_to && !/[,&/]| and /.test(thread.routed_to) ? 's' : ''} this. Open it to send anyway.`
                  : `⏳ Merlin is checking whether ${thread.routed_to || 'they'} already know${thread.routed_to && !/[,&/]| and /.test(thread.routed_to) ? 's' : ''} this before the email goes out.`}
              </span>
            )}
            {reply && (
              <span className={styles.respondedLine} data-testid="row-handoff-reply">
                ✓ {respondedLine(reply)}
                {replyExcerpt ? `: “${replyExcerpt}”` : ''}
              </span>
            )}
            {answer && !reply && (
              <span
                className={answer.beforeSend ? styles.rowSub : styles.respondedLine}
                data-testid="row-ask-answer"
              >
                {answer.beforeSend ? '' : '✓ '}
                {answerWords(answer)}
              </span>
            )}
            {followUp && (
              <span className={styles.rowSub} data-testid="row-followed-up">
                ↻ {followUpWords(followUp)}
              </span>
            )}
            <span className={styles.rowSub}>
              {routedAwayLine(thread) || 'Sent — recipient not recorded'}
            </span>
            {note && (
              <span className={styles.rowNote} data-testid="row-note">
                📝 {note}
              </span>
            )}
            <span className={styles.chips}>
              {thread.routed_at && (
                <span className={`${styles.chip} ${styles.chipAge}`}>
                  {ageLabel(thread.routed_at)} with them
                </span>
              )}
              {/* Answered or not, said plainly (UX round 2026-10-06). It used to
                  read "updated 2h ago", which Merlin's own nightly pass set on
                  most rows at once and read as a reply. No email, no reply to
                  wait for. */}
              {noReply && !unsent && !gated && (
                <span
                  className={styles.chip}
                  data-testid="row-no-reply"
                  title="No email from them about this item since it was sent — neither a reply to the hand-off email nor mail naming the item — and no answer to a question on it. Merlin's own updates to the item do not count."
                >
                  {noReply}
                </span>
              )}
              {sentBy && (
                <span className={styles.chip} data-testid="row-sent-by">
                  {sentBy}
                </span>
              )}
              {missed.length === 0 && !unsent && (delivery?.unconfirmed.length ?? 0) > 0 && (
                <span className={styles.chip} data-testid="row-delivery-unconfirmed">
                  Not known if the email reached{' '}
                  {joinNames((delivery?.unconfirmed ?? []).map((p) => p.name))}
                </span>
              )}
              {stale !== null && !followUp && (
                <span className={`${styles.chip} ${styles.chipStale}`}>
                  Nothing in {stale} days. Chase or pull back?
                </span>
              )}
              <NudgedChip thread={thread} />
            </span>
          </span>
          <span className={styles.rowRight}>
            <ThreadRefLabel thread={thread} />
          </span>
        </button>
        {canPullBack && (
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => onAskPullBack(thread)}
            disabled={busy || confirming}
            title="Route it back to you — it returns to your Triage queue"
          >
            ↩ Pull back
          </button>
        )}
        {actions}
      </div>
      {confirming && (
        <div
          className={styles.rowConfirm}
          role="group"
          aria-label={`Pull ${thread.thread_no} back?`}
          data-testid="confirm-step"
        >
          <span className={styles.rowConfirmText}>
            Pull <ThreadRefLabel thread={thread} /> back to your Triage queue? It will no longer be with{' '}
            {thread.routed_to || 'them'}.
          </span>
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => onPullBack(thread)}
            disabled={busy}
            data-testid="confirm-yes"
          >
            {busy ? 'Pulling it back…' : 'Pull it back'}
          </button>
          <button type="button" className={styles.rowConfirmNo} onClick={onCancel} disabled={busy}>
            Leave it with them
          </button>
        </div>
      )}
      {error && (
        <p className={styles.errorNote} role="alert">
          {error}
        </p>
      )}
      {below}
    </div>
  );
}

export default function QualityRoutedPage() {
  const { invState, userName, deliveryOutcomes, dataRequests, readOnly = false } = useQualityTriage();
  const requests = dataRequests?.requests ?? [];
  const navigate = useNavigate();
  // "With" one person (kanban 68cc2cfb): the list narrowed to the items
  // they hold, in the URL so the back button and a link keep it.
  const [params, setParams] = useSearchParams();
  const who = (params.get('who') ?? '').trim() || null;
  const setWho = (next: string | null) => {
    const q = new URLSearchParams(params);
    if (next) q.set('who', next);
    else q.delete('who');
    trackEvent('filter', 'qcp_routed_person_filter', { who: next });
    setParams(q);
  };
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // The failure sits under the row it belongs to, naming the item.
  const [failure, setFailure] = useState<{ id: string; message: string } | null>(null);

  const listed = routedAwayThreads(invState.threads, userName);
  // The shell already reads them for the "More" alarm; a harness without
  // the shell reads them here.
  const own = useDeliveryOutcomes(deliveryOutcomes ? [] : listed);
  const outcomes = deliveryOutcomes ?? own;
  // Anything that never arrived sorts first (case round, finding 1).
  const away = undeliveredFirst(listed, outcomes);
  // Who the items are with and how many each holds; the list narrowed to one.
  const people = routedAwayPeople(away);
  const shown = who ? routedAwayWith(away, who) : away;
  const openThread = (thread: DoorThreadRead) =>
    navigate(
      threadWorkspacePath(thread, 'tab'),
      fromTab(
        'routed',
        shown.map((t) => t.thread_no),
      ),
    );
  // Nudge (kanban 68cc2cfb): each person an item is with can be nudged once
  // the hand-off reached them; the per-person button covers every item of
  // theirs that can be.
  const nudge = useNudge('routed_away', {
    thread: invState.applyThreadUpdate,
    request: dataRequests?.applyUpdate,
  });
  const personTargets: NudgeTarget[] = who
    ? shown
        .filter((t) => !routedNudgeBlock(t))
        .flatMap((t) => routedTargets(t).filter((target) => namesInclude([target.who], who)))
    : [];

  // Pull back = the same reroute the workspace offers, aimed at the viewer,
  // so the routing playbook learns it and the audit trail records who.
  const pullBack = async (thread: DoorThreadRead) => {
    if (!userName || busyId) return;
    // The step stays open, saying "Pulling it back…", until the server
    // answers (it takes seconds; UX round 2026-10-06).
    setBusyId(thread.id);
    setFailure(null);
    try {
      const result = await rerouteThread(thread.id, userName, 'Pulled back to my queue');
      trackEvent('action', 'qcp_pull_back', { thread_no: thread.thread_no, from: 'routed_away', outcome: 'ok' });
      invState.applyThreadUpdate(result.thread);
      setConfirmingId(null);
    } catch (e) {
      trackEvent('action', 'qcp_pull_back', { thread_no: thread.thread_no, from: 'routed_away', outcome: 'failed' });
      setFailure({
        id: thread.id,
        message: failedActionMessage(`pull ${thread.thread_no} back to your queue`, e),
      });
    } finally {
      setBusyId(null);
    }
  };

  const askPullBack = (thread: DoorThreadRead) => {
    setFailure(null);
    setConfirmingId(thread.id);
  };

  return (
    <div className={styles.page}>
      <p className={styles.legend}>
        Everything sent off your desk and still open with someone else — so
        you always know where it went. Open one to reroute it or ask Merlin
        about it; Pull back returns it to your queue. What you closed lives
        under Done.
      </p>

      <h2 className={styles.lane}>
        Still open with someone else
        {away.length > 0 && <span className={styles.lanePill}>{away.length}</span>}
      </h2>
      {away.length === 0 && (
        <p className={styles.emptyNote}>Nothing is out with anyone right now.</p>
      )}
      {people.length > 0 && (
        <div className={styles.peopleStrip} role="group" aria-label="With" data-testid="routed-people">
          <span className={styles.filterHead}>With</span>
          {people.map((p) => (
            <FilterChip
              key={p.who}
              label={p.who}
              count={p.count}
              on={!!who && namesInclude([p.who], who)}
              action="qcp_routed_person_filter"
              recordValue="show"
              value="with_person"
              onClick={() => setWho(who && namesInclude([p.who], who) ? null : p.who)}
            />
          ))}
          {who && (
            <button
              type="button"
              className={styles.filterClear}
              data-action="qcp_routed_person_filter_clear"
              onClick={() => setWho(null)}
            >
              Show everyone
            </button>
          )}
        </div>
      )}
      {who && (
        <>
          <p className={styles.laneSub} style={{ marginTop: 0 }}>
            Items with <strong>{who}</strong>.
          </p>
          <NudgePerson who={who} targets={personTargets} nudge={nudge} />
          {shown.length === 0 && (
            <p className={styles.emptyNote} data-testid="routed-person-empty">
              Nothing is with {who} right now.
            </p>
          )}
        </>
      )}
      {shown.map((t) => (
        <RoutedRow
          key={t.id}
          thread={t}
          asks={asksForThread(requests, t)}
          userName={userName}
          delivery={outcomes[t.id]}
          canPullBack={!!userName && !readOnly}
          busy={busyId === t.id}
          confirming={confirmingId === t.id}
          error={failure?.id === t.id ? failure.message : null}
          onOpen={openThread}
          onAskPullBack={askPullBack}
          onPullBack={(thread) => void pullBack(thread)}
          onCancel={() => setConfirmingId(null)}
          actions={<NudgeButtons targets={routedTargets(t)} nudge={nudge} blocked={routedNudgeBlock(t)} />}
          below={<NudgeBelow thread={t} nudge={nudge} />}
        />
      ))}
    </div>
  );
}
